
import { CrudCollection } from "../../CrudCollection";
import { filterSetApi } from "../../filters/filterSetApi";
import { readCollectionQueryState } from "../../filters/collectionQueryState";

const FilterSetItem = {
  props: {
    set: {
      type: Object,
      required: true,
    },
    hasCurrentState: {
      type: Boolean,
      required: true,
    },
  },
  render(h) {
    const actions = this.set.can_edit
      ? h(
          "v-list-item-action",
          {
            staticClass: "flex-row",
          },
          [
            h(
              "v-btn",
              {
                attrs: {
                  icon: true,
                  small: true,
                  disabled: !this.hasCurrentState,
                  title: "Overwrite with current filters",
                },
                on: {
                  click: (event) => {
                    event.stopPropagation();
                    this.$emit("overwrite", this.set);
                  },
                },
              },
              [h("v-icon", { attrs: { small: true } }, "mdi-content-save-edit")]
            ),
            h(
              "v-btn",
              {
                attrs: {
                  icon: true,
                  small: true,
                  title: "Rename",
                },
                on: {
                  click: (event) => {
                    event.stopPropagation();
                    this.$emit("rename", this.set);
                  },
                },
              },
              [h("v-icon", { attrs: { small: true } }, "mdi-pencil")]
            ),
            h(
              "v-btn",
              {
                attrs: {
                  icon: true,
                  small: true,
                  title: "Delete",
                  "data-test": `filter-set-delete--${this.set.id}`,
                },
                on: {
                  click: (event) => {
                    event.stopPropagation();
                    this.$emit("delete", this.set);
                  },
                },
              },
              [h("v-icon", { attrs: { small: true } }, "mdi-delete")]
            ),
          ]
        )
      : null;

    return h(
      "v-list-item",
      {
        attrs: {
          "data-test": `filter-set-item--${this.set.id}`,
        },
        on: {
          click: () => this.$emit("load", this.set),
        },
      },
      [h("v-list-item-title", this.set.name), actions]
    );
  },
};

export default {
  components: {
    FilterSetItem,
  },
  props: {
    collection: {
      type: CrudCollection,
      required: true,
    },
    scope: {
      type: String,
      required: true,
    },
    allowGlobal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      sets: [],
      loaded: false,
      loading: false,
      saving: false,
      menuOpen: false,
      dialogOpen: false,
      editingSet: null,
      name: "",
      isGlobal: false,
      errorMessage: "",
      dialogError: "",
    };
  },
  computed: {
    mine() {
      return this.sets.filter((set) => set.is_mine);
    },
    shared() {
      return this.sets.filter((set) => set.is_global);
    },
    currentQueryState() {
      return readCollectionQueryState(this.collection);
    },
    hasCurrentState() {
      return this.collection.hasMeaningfulQueryState;
    },
  },
  watch: {
    menuOpen(open) {
      if (open && !this.loaded) this.loadSets();
    },
    scope() {
      this.sets = [];
      this.loaded = false;
    },
  },
  methods: {
    async loadSets() {
      this.loading = true;
      this.errorMessage = "";

      try {
        this.sets = await filterSetApi.list(this.scope);
        this.loaded = true;
      } catch (error) {
        this.errorMessage = this.errorFromResponse(
          error,
          "Saved filters could not be loaded."
        );
      } finally {
        this.loading = false;
      }
    },
    loadSet(set) {
      this.$emit("load", set.query);
      this.menuOpen = false;
    },
    openCreateDialog() {
      if (!this.hasCurrentState) return;

      this.menuOpen = false;
      this.editingSet = null;
      this.name = "";
      this.isGlobal = false;
      this.dialogError = "";
      this.dialogOpen = true;
    },
    openRenameDialog(set) {
      this.menuOpen = false;
      this.editingSet = set;
      this.name = set.name;
      this.isGlobal = set.is_global;
      this.dialogError = "";
      this.dialogOpen = true;
    },
    closeDialog() {
      if (this.saving) return;
      this.dialogOpen = false;
    },
    async submitDialog() {
      const name = this.name.trim();
      if (!name || (!this.editingSet && !this.hasCurrentState)) return;

      this.saving = true;
      this.dialogError = "";

      try {
        let savedSet;
        if (this.editingSet) {
          savedSet = await filterSetApi.update(this.editingSet.id, { name });
          this.sets = this.sets.map((set) =>
            set.id === savedSet.id ? savedSet : set
          );
        } else {
          savedSet = await filterSetApi.create({
            scope: this.scope,
            name,
            query: this.currentQueryState,
            is_global: this.isGlobal,
          });
          this.sets = [...this.sets, savedSet];
        }

        this.dialogOpen = false;
        this.loaded = true;
      } catch (error) {
        this.dialogError = this.errorFromResponse(
          error,
          "Saved filters could not be updated."
        );
      } finally {
        this.saving = false;
      }
    },
    async overwriteSet(set) {
      if (!this.hasCurrentState) return;

      this.errorMessage = "";
      try {
        await filterSetApi.update(set.id, {
          query: this.currentQueryState,
        });
        this.loaded = false;
        await this.loadSets();
      } catch (error) {
        this.errorMessage = this.errorFromResponse(
          error,
          "Saved filters could not be overwritten."
        );
      }
    },
    async deleteSet(set) {
      if (!window.confirm(`Delete "${set.name}"?`)) return;

      this.errorMessage = "";
      try {
        await filterSetApi.remove(set.id);
        this.sets = this.sets.filter(({ id }) => id !== set.id);
      } catch (error) {
        this.errorMessage = this.errorFromResponse(
          error,
          "Saved filters could not be deleted."
        );
      }
    },
    errorFromResponse(error, fallback) {
      const response = error && (error.response || error);
      const data = response && (response.data || response);
      const errors = data && data.errors;

      if (errors) {
        const firstError = Object.values(errors)[0];
        if (Array.isArray(firstError)) return firstError[0];
        if (firstError) return firstError;
      }

      return (data && data.message) || fallback;
    },
  },
};
